import { useState, useEffect } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Shield, Plus, Pencil, Trash2, RefreshCw, Save, X, Clock, Activity } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { toErr } from '@easyflow/shared';

interface RateLimitConfig {
  id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_slug: string | null;
  endpoint_type: string;
  window_ms: number;
  max_requests: number;
  is_enabled: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

const ENDPOINT_TYPES = ['api', 'auth', 'cdr', 'webhook', 'custom'];

export default function RateLimitsMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [configs, setConfigs] = useState<RateLimitConfig[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingConfig, setEditingConfig] = useState<RateLimitConfig | null>(null);

  const [formData, setFormData] = useState({
    tenant_id: '',
    endpoint_type: 'api',
    window_ms: 900000,
    max_requests: 1000,
    description: '',
    is_enabled: true,
  });

  useEffect(() => {
    void loadConfigs();
    void loadTenants();
  }, []);

  const loadConfigs = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get<RateLimitConfig[]>('/master/rate-limits');
      if (response.success && response.data) {
        setConfigs(response.data);
      }
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to load rate limit configurations',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await apiClient.get<{ tenants: Tenant[] }>('/master/tenants');
      if (response.success && response.data) {
        setTenants(response.data.tenants || []);
      }
    } catch (error: unknown) {
      console.error('Failed to load tenants:', error);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingConfig(null);
    setFormData({
      tenant_id: '',
      endpoint_type: 'api',
      window_ms: 900000,
      max_requests: 1000,
      description: '',
      is_enabled: true,
    });
  };

  const handleEdit = (config: RateLimitConfig) => {
    setEditingConfig(config);
    setIsCreating(false);
    setFormData({
      tenant_id: config.tenant_id || '',
      endpoint_type: config.endpoint_type,
      window_ms: config.window_ms,
      max_requests: config.max_requests,
      description: config.description || '',
      is_enabled: config.is_enabled,
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        tenant_id: formData.tenant_id || null, // Empty string → null for system-wide
      };

      if (editingConfig) {
        await apiClient.put(`/master/rate-limits/${editingConfig.id}`, payload);
        toast({
          title: 'Success',
          description: 'Rate limit updated successfully',
        });
      } else {
        await apiClient.post('/master/rate-limits', payload);
        toast({
          title: 'Success',
          description: 'Rate limit created successfully',
        });
      }

      setIsCreating(false);
      setEditingConfig(null);
      void loadConfigs();
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to save rate limit configuration',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this rate limit configuration?')) return;

    try {
      await apiClient.delete(`/master/rate-limits/${configId}`);
      toast({
        title: 'Success',
        description: 'Rate limit deleted successfully',
      });
      void loadConfigs();
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to delete rate limit configuration',
        variant: 'destructive',
      });
    }
  };

  const formatWindow = (ms: number) => {
    const seconds = ms / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;

    if (hours >= 1) return `${hours}h`;
    if (minutes >= 1) return `${minutes}m`;
    return `${seconds}s`;
  };

  const getEndpointBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      api: 'bg-blue-100 text-blue-700 border-blue-300',
      auth: 'bg-red-100 text-red-700 border-red-300',
      cdr: 'bg-green-100 text-green-700 border-green-300',
      webhook: 'bg-purple-100 text-purple-700 border-purple-300',
      custom: 'bg-muted text-foreground dark:text-foreground border-border dark:border-border',
    };
    return colors[type] || colors.custom;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonTable rows={6} />
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              API Rate Limiting
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Manage API request throttling and limits</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadConfigs} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New Config
            </Button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <GlassCard className="border-l-4 border-l-blue-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Total Configs
              </GlassCardDescription>
              <GlassCardTitle className="text-3xl">{configs.length}</GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>

          <GlassCard className="border-l-4 border-l-green-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Active
              </GlassCardDescription>
              <GlassCardTitle className="text-3xl text-green-600">
                {configs.filter(c => c.is_enabled).length}
              </GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>

          <GlassCard className="border-l-4 border-l-gray-500">
            <GlassCardHeader className="pb-3">
              <GlassCardDescription className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                System-Wide
              </GlassCardDescription>
              <GlassCardTitle className="text-3xl text-muted-foreground dark:text-muted-foreground/60">
                {configs.filter(c => c.tenant_id === null).length}
              </GlassCardTitle>
            </GlassCardHeader>
          </GlassCard>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingConfig) && (
          <GlassCard className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/20">
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                {editingConfig ? 'Edit Rate Limit' : 'Create Rate Limit'}
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Tenant Scope *</label>
                  <select
                    value={formData.tenant_id}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    disabled={!!editingConfig}
                  >
                    <option value="">System-Wide (All Tenants)</option>
                    {tenants.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.slug})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Endpoint Type *</label>
                  <select
                    value={formData.endpoint_type}
                    onChange={(e) => setFormData({ ...formData, endpoint_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    disabled={!!editingConfig}
                  >
                    {ENDPOINT_TYPES.map(type => (
                      <option key={type} value={type}>{type.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Window (ms) *</label>
                  <Input
                    type="number"
                    value={formData.window_ms}
                    onChange={(e) => setFormData({ ...formData, window_ms: parseInt(e.target.value) })}
                    placeholder="900000"
                  />
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-1">{formatWindow(formData.window_ms)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Max Requests *</label>
                  <Input
                    type="number"
                    value={formData.max_requests}
                    onChange={(e) => setFormData({ ...formData, max_requests: parseInt(e.target.value) })}
                    placeholder="1000"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                      className="rounded border-border"
                    />
                    <span className="text-sm font-medium">Enabled</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Optional description of this rate limit configuration"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave}>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingConfig(null);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>
        )}

        {/* Configs Table */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Rate Limit Configurations</GlassCardTitle>
            <GlassCardDescription>
              Manage API request throttling for different endpoint types
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Endpoint Type</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Window</TableHead>
                    <TableHead>Max Requests</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                        No rate limit configurations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    configs.map((config) => (
                      <TableRow key={config.id}>
                        <TableCell>
                          <Badge className={getEndpointBadgeColor(config.endpoint_type)}>
                            {config.endpoint_type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {config.tenant_name ? (
                            <div>
                              <div className="font-medium text-sm">{config.tenant_name}</div>
                              <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">{config.tenant_slug}</div>
                            </div>
                          ) : (
                            <Badge variant="secondary">System-Wide</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{formatWindow(config.window_ms)}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold">{config.max_requests.toLocaleString()}</span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground max-w-xs truncate">
                            {config.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {config.is_enabled ? (
                            <Badge variant="default" className="bg-green-100 text-green-700">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Disabled</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(config)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(config.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
